import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  Download, 
  Image, 
  ZoomIn, 
  ZoomOut, 
  RotateCw, 
  Maximize,
  Palette,
  Crop,
  Edit,
  Save,
  Undo
} from 'lucide-react';

const ImageViewer = ({ file, previewUrl, onDownload, onSave, isLoading }) => {
  const [zoom, setZoom] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 5.0));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const resetFilters = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
  };

  const getImageStyle = () => ({
    transform: `rotate(${rotation}deg) scale(${zoom})`,
    transformOrigin: 'center center',
    filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`,
    transition: 'filter 0.3s ease, transform 0.3s ease'
  });

  const getImageDimensions = () => {
    if (imageRef.current) {
      return {
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      };
    }
    return { width: 0, height: 0 };
  };

  const saveEditedImage = async () => {
    if (!onSave) {
      setIsEditing(false);
      return;
    }

    try {
      // Create a canvas to apply filters and get the edited image
      const canvas = canvasRef.current || document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = imageRef.current;

      if (!img) return;

      // Set canvas dimensions to match image
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Apply filters to canvas
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
      
      // Draw the image with rotation
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
      ctx.restore();

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (blob) {
          // Call the onSave callback with the edited image blob
          await onSave(blob);
          setIsEditing(false);
        }
      }, `image/${file.file_type}`, 0.95);
    } catch (error) {
      console.error('Error saving edited image:', error);
      alert('Failed to save edited image');
    }
  };

  const { width, height } = getImageDimensions();

  return (
    <div className="space-y-4">
      {/* Image Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Image className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-gray-900">{file.original_filename}</h3>
            <p className="text-sm text-gray-500">
              {file.file_type.toUpperCase()} Image • {width}×{height} • {Math.round(file.file_size / 1024)} KB
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          ) : (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  resetFilters();
                }}
              >
                Cancel
              </Button>
              <Button 
                size="sm"
                onClick={saveEditedImage}
                disabled={isLoading}
                className="bg-cyan-500 hover:bg-cyan-600"
              >
                <Save className="h-4 w-4 mr-1" />
                Save Changes
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={onDownload}
          >
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Image Controls */}
      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
            disabled={zoom <= 0.25}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          
          <span className="px-3 py-1 bg-white rounded text-sm font-medium min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomIn}
            disabled={zoom >= 5.0}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleRotate}
          >
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setZoom(1.0)}
          >
            Fit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetFilters}
          >
            <Undo className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>

      {/* Image Editing Controls */}
      {isEditing && (
        <div className="bg-blue-50 p-4 rounded-lg space-y-3">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Image Adjustments
          </h4>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brightness: {brightness}%
              </label>
              <input
                type="range"
                min="0"
                max="200"
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contrast: {contrast}%
              </label>
              <input
                type="range"
                min="0"
                max="200"
                value={contrast}
                onChange={(e) => setContrast(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Saturation: {saturation}%
              </label>
              <input
                type="range"
                min="0"
                max="200"
                value={saturation}
                onChange={(e) => setSaturation(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Display */}
      <div className="border rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center" style={{ minHeight: '500px' }}>
        {previewUrl ? (
          <div className="p-4 max-w-full max-h-full overflow-auto">
            <img
              ref={imageRef}
              src={previewUrl}
              alt={file.original_filename}
              style={getImageStyle()}
              className="max-w-none"
              onError={() => console.error('Failed to load image')}
            />
          </div>
        ) : (
          <div className="text-center p-8">
            <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Loading image...</p>
          </div>
        )}
      </div>

      {/* Image Information */}
      <div className="flex items-center justify-between text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-4">
          <span>Format: {file.file_type.toUpperCase()}</span>
          <span>Resolution: {width}×{height}</span>
          <span>Size: {Math.round(file.file_size / 1024)} KB</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
          {rotation > 0 && <span>Rotated: {rotation}°</span>}
        </div>
        <div>
          {isEditing ? (
            <span className="text-blue-600 font-medium">● Editing mode</span>
          ) : (
            <span className="text-green-600">● View mode</span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setZoom(0.5)}
          className="text-xs"
        >
          50%
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setZoom(1.0)}
          className="text-xs"
        >
          100%
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setZoom(2.0)}
          className="text-xs"
        >
          200%
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => {
            setZoom(1.0);
            setRotation(0);
            resetFilters();
          }}
          className="text-xs"
        >
          Reset All
        </Button>
      </div>
    </div>
  );
};

export default ImageViewer;